import React, { useState, useMemo, useEffect } from 'react';
import useParkingAuth from '../../../hooks/admin/useParkingAuth';
import { useParkingSpaces } from '../../../hooks/admin/useParkingSpaces';
import ParkingSpaceTable from '../../../components/admin/parking/ParkingSpaceTable';
import ParkingSpaceForm from '../../../components/admin/parking/ParkingSpaceForm';
import ParkingStatusFilter from '../../../components/admin/parking/ParkingStatusFilter';
import ParkingExpiryFilter from '../../../components/admin/parking/ParkingExpiryFilter';
import { ParkingSpaceWithContract } from '../../../types/parkingSpace';

const ParkingManagePage: React.FC = () => {
  const { loading: authLoading, hasAccess } = useParkingAuth();
  const { 
    spaces, 
    allSpaces,
    loading, 
    connected,
    error,
    searchTerm, 
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    expiryFilter,
    setExpiryFilter,
    addSpace, 
    updateSpace, 
    deleteSpace 
  } = useParkingSpaces();

  const [showForm, setShowForm] = useState(false);
  const [editingSpace, setEditingSpace] = useState<ParkingSpaceWithContract | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUpdateToast, setShowUpdateToast] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // 만료일 계산 헬퍼 함수
  const getDaysUntilExpiry = (endDate: Date) => {
    const today = new Date();
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // 전체 주차면 목록에서 상태별 카운트 계산 (필터링 이전 데이터 사용)
  const spaceCounts = useMemo(() => {
    const counts = {
      all: allSpaces.length,
      occupied: 0,
      preparing: 0,
      vacant: 0,
      contracting: 0
    };

    allSpaces.forEach((space: ParkingSpaceWithContract) => {
      counts[space.status]++;
    });

    return counts;
  }, [allSpaces]);

  // 만료일별 카운트 계산
  const expiryCounts = useMemo(() => {
    const occupiedSpaces = allSpaces.filter(space => 
      space.status === 'occupied' && space.currentContract?.usagePeriod?.endDate
    );

    const counts = {
      all: occupiedSpaces.length,
      expired: 0,
      expiringSoon: 0,
      expiringMonth: 0
    };

    occupiedSpaces.forEach((space: ParkingSpaceWithContract) => {
      if (space.currentContract?.usagePeriod?.endDate) {
        const daysUntilExpiry = getDaysUntilExpiry(space.currentContract.usagePeriod.endDate);
        
        if (daysUntilExpiry < 0) {
          counts.expired++;
        } else if (daysUntilExpiry <= 7) {
          counts.expiringSoon++;
        } else if (daysUntilExpiry <= 30) {
          counts.expiringMonth++;
        }
      }
    });

    return counts;
  }, [allSpaces]);

  // 실시간 업데이트 감지
  useEffect(() => {
    if (connected && !loading) {
      if (isInitialLoad) {
        // 초기 로드 완료
        setIsInitialLoad(false);
      } else if (allSpaces.length > 0) {
        // 실제 업데이트 발생 시에만 토스트 표시
        setShowUpdateToast(true);
        const timer = setTimeout(() => setShowUpdateToast(false), 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [allSpaces, connected, loading, isInitialLoad]);

  if (authLoading) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#155acb]"></div>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return null; // useParkingAuth에서 자동으로 리다이렉트 처리
  }

  const handleAddSpace = () => {
    setEditingSpace(undefined);
    setShowForm(true);
  };

  const handleEditSpace = (space: ParkingSpaceWithContract) => {
    setEditingSpace(space);
    setShowForm(true);
  };

  const handleFormSubmit = async (formData: any) => {
    setIsSubmitting(true);
    try {
      let success = false;
      if (editingSpace) {
        success = await updateSpace(editingSpace.id, formData);
      } else {
        success = await addSpace(formData);
      }
      
      if (success) {
        setShowForm(false);
        setEditingSpace(undefined);
      }
      return success;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSpace = async (spaceId: string) => {
    if (window.confirm('정말로 이 주차면을 삭제하시겠습니까?\n연관된 계약 정보도 함께 처리됩니다.')) {
      await deleteSpace(spaceId);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingSpace(undefined);
  };

  return (
    <div className="w-full min-h-screen bg-[#f7f9fd]">
      {/* 실시간 업데이트 토스트 */}
      {showUpdateToast && (
        <div className="fixed top-4 right-4 z-50 bg-[#155acb] text-white px-4 py-2 rounded-sm shadow-lg flex items-center space-x-2 animate-fade-in-out">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span className="text-sm font-['Pretendard']">데이터가 업데이트되었습니다</span>
        </div>
      )}

      {/* 본문 컨테이너 - 중앙 정렬 */}
      <div className="max-w-[1600px] mx-auto relative pt-8 px-6">
        {/* 헤더 섹션 */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
              주차장 관리
            </div>
            {/* 실시간 연결 상태 표시 */}
            <div className="flex items-center space-x-2 text-sm">
              <div className="flex items-center space-x-1">
                <div className={`w-2 h-2 rounded-full ${
                  loading ? 'bg-yellow-400' : 
                  connected ? 'bg-green-400' : 
                  'bg-red-400'
                } ${loading || !connected ? 'animate-pulse' : ''}`}></div>
                <span className="text-[#6b7684] font-['Pretendard']">
                  {loading ? '연결 중...' : 
                   connected ? '실시간 연결됨' : 
                   '연결 끊김'}
                </span>
              </div>
            </div>
          </div>
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
        </div>

        {/* 안내 메시지 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-6 bg-[#f2f6fb] border-l-4 border-[#155acb] rounded-r-sm">
            <p className="text-[#1e2c49] text-lg font-medium font-['Pretendard']">
              주차장 현황 및 관리를 위한 페이지입니다.
            </p>
          </div>
        </div>

        {/* 오류 메시지 표시 */}
        {error && (
          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className="p-6 bg-red-50 border-l-4 border-red-500 rounded-r-sm">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800 font-['Pretendard']">{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {!showForm ? (
          <>
            {/* 필터 및 검색 섹션 카드 */}
            <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
              <div className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9">
                    필터 및 관리
                  </h2>
                  <button
                    onClick={handleAddSpace}
                    className="w-[100px] h-11 bg-[#155acb] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#155acb]/90 transition-colors"
                  >
                    주차면 추가
                  </button>
                </div>
                <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>
                
                <div className="grid grid-cols-1 lg:grid-cols-[2fr_2fr_1.5fr] gap-8">
                  {/* 상태별 필터 */}
                  <div>
                    <h3 className="text-[#1e2c49] text-lg font-semibold font-['Pretendard'] mb-4">
                      주차면 상태
                    </h3>
                    <ParkingStatusFilter
                      currentFilter={statusFilter}
                      onFilterChange={setStatusFilter}
                      spaceCounts={spaceCounts}
                    />
                  </div>
                  
                  {/* 계약 만료일 필터 */}
                  <div>
                    <h3 className="text-[#1e2c49] text-lg font-semibold font-['Pretendard'] mb-4">
                      계약 만료일
                    </h3>
                    <ParkingExpiryFilter
                      currentFilter={expiryFilter}
                      onFilterChange={setExpiryFilter}
                      expiryCounts={expiryCounts}
                    />
                  </div>

                  {/* 검색 */}
                  <div>
                    <h3 className="text-[#1e2c49] text-lg font-semibold font-['Pretendard'] mb-4">
                      주차면 검색
                    </h3>
                    <input
                      type="text"
                      placeholder="주차면번호, 이용자명, 연락처, 소속, 차량정보 등"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-3 py-3 text-[#1e2c49] font-['Pretendard'] text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 주차면 목록 카드 */}
            <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
              <div className="p-8">
                <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                  주차면 목록
                </h2>
                <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>
                
                <ParkingSpaceTable
                  spaces={spaces}
                  onEdit={handleEditSpace}
                  onDelete={handleDeleteSpace}
                  loading={loading}
                />
              </div>
            </div>
          </>
        ) : (
          /* 주차면 추가/수정 폼 카드 */
          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className="p-8">
              <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                {editingSpace ? '주차면 수정' : '주차면 추가'}
              </h2>
              <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>
              
              <ParkingSpaceForm
                space={editingSpace}
                onSubmit={handleFormSubmit}
                onCancel={handleFormCancel}
                isSubmitting={isSubmitting}
              />
            </div>
          </div>
        )}

        {/* 하단 여백 */}
        <div className="h-16"></div>
      </div>
    </div>
  );
};

export default ParkingManagePage; 